
import { memo } from 'react';
import { formatFullName } from '@/lib/format';
import { getPrayerCategories } from '@/lib/categories';
import { cn } from '@/lib/utils';
import { Badge, Button, Card, CardFooter, getCategoryBadgeVariant } from '@/components/ui';
import type { QueueItem } from '@/hooks/useProducerQueue';

export interface QueueItemCardProps {
  item: QueueItem;
  position: number;
  queueType?: 'prayer' | 'praise';
  onRemove?: (prayerId: string) => void;
  isCompact?: boolean;
  /** Which prayer request is currently "live" (prayer column only) */
  livePrayerRequestId?: string | null;
  /** Which prayer the shuffle will show next (prayer column only) */
  shufflePrayerNextRequestId?: string | null;
  /** Whether to show "Next in shuffle" (only when user manually displayed in this column) */
  showNextInShuffle?: boolean;
  /** Set this prayer as live (prayer column only) */
  onDisplayLive?: (prayerRequestId: string) => void;
  /** Which praise is currently "live" (praise column only) */
  livePraiseRequestId?: string | null;
  /** Which praise the shuffle will show next (praise column only) */
  shufflePraiseNextRequestId?: string | null;
  /** Set this praise as live (praise column only) */
  onDisplayLivePraise?: (prayerRequestId: string) => void;
  /** Edit this prayer (opens editor on producer page) */
  onEdit?: (prayer: import('@/types').PrayerRequest) => void;
  /** Prayer ID -> { userId, userName } for who is currently editing (real-time) */
  editingBy?: Record<string, { userId: string; userName: string }>;
  /** Current user ID so we can allow editing when the editor is self */
  currentUserId?: string | null;
}

function QueueItemCardComponent({
  item,
  position,
  queueType,
  onRemove,
  isCompact = false,
  livePrayerRequestId,
  shufflePrayerNextRequestId,
  showNextInShuffle = false,
  onDisplayLive,
  livePraiseRequestId,
  shufflePraiseNextRequestId,
  onDisplayLivePraise,
  onEdit,
  editingBy = {},
  currentUserId = null,
}: QueueItemCardProps) {
  const { prayerRequest, isDisplayed, prayerRequestId } = item;
  if (!prayerRequest) {
    return (
      <Card padding="md" className="border-dashed border-slate-300 dark:border-slate-600">
        <p className="text-sm text-slate-500 dark:text-slate-400">Incomplete queue row</p>
      </Card>
    );
  }
  const editor = editingBy[prayerRequestId];
  const isEditedByOther = editor && editor.userId !== currentUserId;
  const prayerCategories = getPrayerCategories(prayerRequest ?? {});
  const categoryLabels = prayerCategories.length > 0 ? prayerCategories : ['General'];
  const displayName = prayerRequest?.isAnonymous
    ? 'Anonymous'
    : (formatFullName(prayerRequest?.firstName ?? null, prayerRequest?.lastName ?? null) || 'Anonymous');
  const isLive =
    (queueType === 'prayer' && prayerRequestId === livePrayerRequestId) ||
    (queueType === 'praise' && prayerRequestId === livePraiseRequestId);
  const isNextInShuffle =
    showNextInShuffle &&
    !isLive &&
    ((queueType === 'prayer' && prayerRequestId === shufflePrayerNextRequestId) ||
      (queueType === 'praise' && prayerRequestId === shufflePraiseNextRequestId));
  const onDisplay = queueType === 'prayer' ? onDisplayLive : queueType === 'praise' ? onDisplayLivePraise : undefined;

  return (
    <Card
      padding={isCompact ? 'none' : 'md'}
      className={cn(
        'transition-all duration-200',
        isLive
          ? 'border-l-4 border-l-primary-500 bg-primary-50/40 dark:bg-primary-900/10'
          : isNextInShuffle
            ? 'border-l-4 border-l-slate-400 border-dashed'
            : 'hover:border-primary-300 dark:hover:border-primary-600',
        !isLive && 'hover:shadow-md',
        isDisplayed &&
          !isLive &&
          'bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80 opacity-[0.88]',
        isCompact && 'p-3'
      )}
    >
      <div className={cn('flex h-full flex-col', isCompact ? 'space-y-1' : 'space-y-2')}>
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {categoryLabels.map((cat) => (
              <Badge key={cat} variant={getCategoryBadgeVariant(cat)} className="capitalize">
                {cat}
              </Badge>
            ))}
            <span className={cn('text-slate-600 dark:text-slate-400', isDisplayed && !isLive && 'text-slate-500 dark:text-slate-500')}>
              {displayName}
            </span>
            {!isLive && isDisplayed && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600 dark:bg-slate-800/80 dark:text-slate-300 border border-slate-200/80 dark:border-slate-600/80">
                <span className="h-1.5 w-1.5 rounded-full bg-slate-400 dark:bg-slate-500" />
                Displayed
              </span>
            )}
          </div>
          {isLive && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-600 text-white dark:bg-primary-500 dark:text-slate-900 shadow-sm">
              <span className="relative flex h-1.5 w-1.5 overflow-hidden rounded-full">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
              </span>
              Live
            </span>
          )}
          {!isLive && isNextInShuffle && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600">
              Next in shuffle
            </span>
          )}
        </div>

        <p
          className={cn(
            'mb-3 text-base text-slate-800 dark:text-slate-200',
            isCompact ? 'line-clamp-2' : 'line-clamp-3',
            isDisplayed && !isLive && 'text-slate-600 dark:text-slate-400'
          )}
        >
          {prayerRequest?.body ?? ''}
        </p>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500 dark:text-slate-400">
          <span>
            Displayed {(item.displayCount ?? 0)} time{(item.displayCount ?? 0) === 1 ? '' : 's'}
          </span>
          {editor && (
            <span className="font-medium text-amber-600 dark:text-amber-400">
              Currently being edited by {editor.userName}
            </span>
          )}
        </div>

        <CardFooter className="mt-4 justify-between">
          <div className="flex items-center gap-2 shrink-0">
            {onDisplay && (
              <Button
                onClick={() => onDisplay(prayerRequestId)}
                size={isCompact ? 'sm' : 'md'}
                variant="ghost"
                className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
                leftIcon={
                  isDisplayed ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  )
                }
              >
                {isDisplayed ? 'Display Again' : 'Display Live'}
              </Button>
            )}
            {onEdit && (
              <Button
                onClick={() => onEdit(prayerRequest)}
                variant="ghost"
                size={isCompact ? 'sm' : 'md'}
                disabled={!!isEditedByOther}
                aria-label={isEditedByOther ? `Being edited by ${editor?.userName}` : 'Edit prayer'}
                leftIcon={
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                }
              >
                Edit
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {onRemove && (
              <Button
                onClick={() => onRemove(prayerRequestId)}
                variant="ghost"
                size={isCompact ? 'sm' : 'md'}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                leftIcon={
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                }
              >
                Remove
              </Button>
            )}
          </div>
        </CardFooter>
      </div>
    </Card>
  );
}

export const QueueItemCard = memo(QueueItemCardComponent);
